/* eslint-disable camelcase */
import { FC, useEffect, useState } from 'react';

import { useGetMe } from 'hooks/use-get-me';
import { useOrganizations } from 'hooks/use-organizations';
import { detectProduct, getProductName } from 'helpers/product';
import { useProducts } from 'hooks/use-products';
import { getItem } from 'helpers/storage/local-storage';
import { isStateJwtToken, navigateToSelectedProduct } from 'helpers/navigation';
import Location from 'services/Location';
import { parseJwt } from 'helpers/token';

import Amplitude from 'services/Amplitude';
import { OrganizationChooser } from './OrganizationChooser';
import { JoinProduct } from './join-product/JoinProduct';
import { JoinProductBlocked } from './join-product/JoinProductBlocked';
import { getShouldDisplayExternalChooser } from './chooser-layout/helpers';

export const Chooser: FC = () => {
  const [organizationWithJoinBlocked, setOrganizationWithJoinBlocked] = useState<string>(null);
  const [displayExternalChooser, setDisplayExternalChooser] = useState(false);
  const { handleRedirectToProduct, isFetchingProducts, products } = useProducts();
  const {
    isAccountWhitelisted,
    isAccountWhitelistedElsewhere,
    isAccountVerified,
    isFetching: isFetchingMe,
    email,
    roles,
  } = useGetMe();
  const { organizations, isFetching: isFetchingOrganizations } = useOrganizations();

  const isFetching = isFetchingMe || isFetchingOrganizations;
  const state = Location.getHashParam('state') || Location.getUrlParam('state');
  const isSigningUp = getItem('signup') === 1;

  useEffect(() => {
    if (isFetchingProducts) {
      return;
    }

    const token = state && parseJwt(state);
    const originalClientId = token?.client_id;

    const shouldDisplayExternalChooser = getShouldDisplayExternalChooser(
      originalClientId,
      products
    );

    if (!shouldDisplayExternalChooser) {
      setDisplayExternalChooser(false);
    } else {
      setDisplayExternalChooser(true);
    }
  }, [products, isFetchingProducts, state]);

  useEffect(() => {
    if (displayExternalChooser) {
      Amplitude.logEvent(null, 'EXTERNAL_APP_CHOOSER_DISPLAYED');
    }
  }, [displayExternalChooser]);

  if (isFetching || isFetchingOrganizations || !organizations?.length) {
    return null;
  }

  const currentProduct = getProductName(detectProduct());

  if (organizationWithJoinBlocked) {
    return <JoinProductBlocked organizationId={organizationWithJoinBlocked} />;
  }

  if (displayExternalChooser || currentProduct === 'Undefined') {
    return (
      <OrganizationChooser
        email={email}
        isAccountVerified={isAccountVerified}
        isAccountWhitelisted={isAccountWhitelisted}
        isAccountWhitelistedElsewhere={isAccountWhitelistedElsewhere}
        onJoinBlocked={setOrganizationWithJoinBlocked}
      />
    );
  }

  const isCurrentProductOpen = organizations[0].products.some(
    ({ name }) => currentProduct === name
  );
  const roleInProduct = roles.filter(
    ({ product, role }) => product === currentProduct && role !== 'inviter'
  );
  const hasRoleInProduct = !!roleInProduct;

  if (organizations.length === 1) {
    if (isAccountWhitelisted) {
      if (isCurrentProductOpen && !hasRoleInProduct) {
        return (
          <JoinProduct
            currentProduct={currentProduct}
            organizationId={organizations[0].organization_id}
          />
        );
      }

      handleRedirectToProduct(currentProduct, organizations[0].organization_id);
      return null;
    }
  }

  if (currentProduct === 'ChatBot') {
    return (
      <OrganizationChooser
        email={email}
        isAccountVerified={isAccountVerified}
        isAccountWhitelisted={isAccountWhitelisted}
        isAccountWhitelistedElsewhere={isAccountWhitelistedElsewhere}
        onJoinBlocked={setOrganizationWithJoinBlocked}
      />
    );
  }

  if (!isSigningUp && isStateJwtToken(state)) {
    if (!hasRoleInProduct) {
      return (
        <OrganizationChooser
          email={email}
          isAccountVerified={isAccountVerified}
          isAccountWhitelisted={isAccountWhitelisted}
          isAccountWhitelistedElsewhere={isAccountWhitelistedElsewhere}
          onJoinBlocked={setOrganizationWithJoinBlocked}
        />
      );
    }

    if (roleInProduct.length === 1) {
      navigateToSelectedProduct(email, roleInProduct[0].organization_id);
      return null;
    }
  }

  return (
    <OrganizationChooser
      email={email}
      isAccountVerified={isAccountVerified}
      isAccountWhitelisted={isAccountWhitelisted}
      isAccountWhitelistedElsewhere={isAccountWhitelistedElsewhere}
      onJoinBlocked={setOrganizationWithJoinBlocked}
    />
  );
};
